import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { MoreVertical, Star, Share2, Pencil, Trash2, FolderInput, Calendar, FileText, FileEdit } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Caso } from '@/types/casos';
import { useUpdateCaso, useDeleteCaso } from '@/hooks/useCasos';
import { useAuth } from '@/context/AuthContext';
import { EditCasoDialog } from './EditCasoDialog';
import { MoveCasoDialog } from './MoveCasoDialog';

interface CasoCardProps {
  caso: Caso;
}

const gradients: Record<string, string> = {
  'gradient-1': 'bg-gradient-to-br from-blue-500 to-purple-600',
  'gradient-2': 'bg-gradient-to-br from-pink-500 to-rose-600',
  'gradient-3': 'bg-gradient-to-br from-green-500 to-teal-600',
  'gradient-4': 'bg-gradient-to-br from-orange-500 to-red-600',
  'gradient-5': 'bg-gradient-to-br from-indigo-500 to-blue-600',
  'gradient-6': 'bg-gradient-to-br from-purple-500 to-pink-600',
};

const getIcon = (iconName: string = 'scale') => {
  // Converte 'kebab-case' para 'PascalCase' para match com exports do Lucide
  const pascalCase = iconName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');
  
  const IconComponent = (LucideIcons as any)[pascalCase];
  return IconComponent || LucideIcons.Scale;
};

export function CasoCard({ caso }: CasoCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const updateCaso = useUpdateCaso();
  const deleteCaso = useDeleteCaso();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  const handleToggleFeatured = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await updateCaso.mutateAsync({
      id: caso.id,
      is_featured: !caso.is_featured,
    });
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditDialogOpen(true);
  };

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Tem certeza que deseja excluir este caso?')) {
      await deleteCaso.mutateAsync(caso.id);
    }
  };

  const handleCardClick = () => {
    navigate(`/casos/${caso.id}`);
  };

  return (
    <Card
      className="group relative overflow-hidden cursor-pointer transition-all hover:shadow-lg hover:-translate-y-1"
      onClick={handleCardClick}
    >
      <div
        className={`h-[3rem] ${gradients[caso.cover_gradient] || gradients['gradient-1']} relative`}
      >
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {caso.is_featured && (
            <div className="bg-white/90 dark:bg-black/90 rounded-full p-1.5">
              <Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />
            </div>
          )}
          {caso.is_shared && (
            <div className="bg-white/90 dark:bg-black/90 rounded-full p-1.5">
              <Share2 className="h-3.5 w-3.5 text-blue-500" />
            </div>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 bg-white/90 dark:bg-black/90 hover:bg-white dark:hover:bg-black"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleToggleFeatured}>
                <Star className="h-4 w-4 mr-2" />
                {caso.is_featured ? 'Remover destaque' : 'Adicionar destaque'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleEdit}>
                <Pencil className="h-4 w-4 mr-2" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                setMoveDialogOpen(true);
              }}>
                <FolderInput className="h-4 w-4 mr-2" />
                Mover Caso
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="absolute -bottom-5 left-4">
          <Avatar className="h-12 w-12 border-4 border-background">
            <AvatarFallback className="bg-primary text-primary-foreground">
              {(() => {
                const Icon = getIcon(caso.icon);
                return <Icon className="h-6 w-6" />;
              })()}
            </AvatarFallback>
          </Avatar>
        </div>
      </div>

      <div className="p-4 pt-8 space-y-2">
        <h3 className="font-semibold text-lg line-clamp-1">{caso.title}</h3>
        
        {caso.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {caso.description}
          </p>
        )}

        <div className="flex items-center justify-between pt-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>
              {formatDistanceToNow(new Date(caso.created_at), {
                addSuffix: true,
                locale: ptBR,
              })}
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <FileText className="h-3.5 w-3.5" />
              <span>{caso.sources_count} fontes</span>
            </div>
            <div className="flex items-center gap-1">
              <FileEdit className="h-3.5 w-3.5" />
              <span>{caso.documents_count || 0} docs</span>
            </div>
          </div>
        </div>
      </div>

      <div onClick={(e) => e.stopPropagation()}>
        <EditCasoDialog 
          open={editDialogOpen} 
          onOpenChange={setEditDialogOpen}
          caso={caso}
        />
        <MoveCasoDialog
          open={moveDialogOpen}
          onOpenChange={setMoveDialogOpen}
          casoId={caso.id}
          currentFolderId={caso.folder_id}
        />
      </div>
    </Card>
  );
}
